import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiAlertCircle } from 'react-icons/fi';
import { Card } from '../../../components/ui/Kit';
import SafeIcon from '../../../common/SafeIcon';
import {
  calculateTotalAnnualIncome,
  calculateNetIncome,
  calculateInsuranceNeed,
  calculateEstateNeedsAttentionCount,
  getKeyRecommendations,
} from '../../../lib/financialAnalysisUtils';

const ESTATE_ITEM_ORDER = [
  'will',
  'powerOfAttorney',
  'healthcareDirective',
  'trust',
  'beneficiaryDesignations',
  'guardianship',
  'emergencyFund',
  'taxPlanning',
];

const ESTATE_ITEM_LABELS = {
  will: 'Will',
  powerOfAttorney: 'Power of Attorney',
  healthcareDirective: 'Healthcare Directive',
  trust: 'Trust',
  beneficiaryDesignations: 'Beneficiary Designations',
  guardianship: 'Guardianship',
  emergencyFund: 'Emergency Fund',
  taxPlanning: 'Tax Planning',
};

const DEFAULT_FINAL_EXPENSES = 25000;

const cellInput =
  'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatDisplayDate(val) {
  if (!val) return null;
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function resolveFinalExpenses(override) {
  if (override === '' || override == null) return DEFAULT_FINAL_EXPENSES;
  return Number(override);
}

function itemPayload(row) {
  return {
    needs_attention: Boolean(row?.needs_attention),
    last_updated: row?.last_updated || null,
    notes: row?.notes ?? '',
  };
}

export default function EstatePlanningTab({
  analysis,
  incomeSources,
  expenses,
  assets,
  liabilities,
  insurancePolicies,
  estateChecklist,
  setEstateChecklist,
  scheduleEstateItemSave,
  scheduleParentSave,
  parentFieldsRef,
  estateRef,
}) {
  const [expandedNotes, setExpandedNotes] = useState(() => new Set());
  const [, bump] = useState(0);

  const orderedItems = useMemo(() => {
    const byKey = new Map((estateChecklist || []).map((item) => [item.item_key, item]));
    return ESTATE_ITEM_ORDER.map((key) => byKey.get(key)).filter(Boolean);
  }, [estateChecklist]);

  const needsAttentionCount = calculateEstateNeedsAttentionCount(estateChecklist);
  const needsAttentionPct = (needsAttentionCount / 8) * 100;

  const legacyWishes =
    parentFieldsRef.current?.legacy_wishes !== undefined
      ? parentFieldsRef.current.legacy_wishes
      : analysis.legacy_wishes ?? '';

  const recommendations = useMemo(() => {
    const annualIncome = calculateTotalAnnualIncome(incomeSources);
    const netIncome = calculateNetIncome(incomeSources, expenses);
    const years =
      parentFieldsRef.current?.years_to_replace ?? analysis.years_to_replace ?? 20;
    const finalOverride =
      parentFieldsRef.current?.final_expenses_override !== undefined
        ? parentFieldsRef.current.final_expenses_override
        : analysis.final_expenses_override;
    const educationFund =
      parentFieldsRef.current?.education_fund !== undefined
        ? parentFieldsRef.current.education_fund
        : analysis.education_fund ?? 0;

    const need = calculateInsuranceNeed({
      incomeSources,
      assets,
      insurancePolicies,
      yearsToReplace: years || 20,
      finalExpenses: resolveFinalExpenses(finalOverride),
      educationFund: Number(educationFund) || 0,
    });

    const totalAssets = (assets || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalLiabilities = (liabilities || []).reduce(
      (sum, r) => sum + (Number(r.amount) || 0),
      0,
    );

    return getKeyRecommendations({
      netIncome,
      annualIncome,
      insuranceGap: need.coverageGap,
      estateChecklist,
      totalAssets,
      totalLiabilities,
    });
  }, [
    incomeSources,
    expenses,
    assets,
    liabilities,
    insurancePolicies,
    estateChecklist,
    analysis.years_to_replace,
    analysis.final_expenses_override,
    analysis.education_fund,
  ]);

  const updateItemLocal = (itemId, patch) => {
    setEstateChecklist((prev) => {
      const next = prev.map((r) => (r.id === itemId ? { ...r, ...patch } : r));
      if (estateRef) estateRef.current = next;
      return next;
    });
    scheduleEstateItemSave(itemId, () => {
      const row = (estateRef?.current || []).find((r) => r.id === itemId);
      return itemPayload(row || patch);
    });
  };

  const onToggleNeedsAttention = (item, checked) => {
    updateItemLocal(item.id, {
      needs_attention: checked,
      last_updated: checked ? todayIsoDate() : null,
    });
  };

  const onNotesChange = (item, notes) => {
    updateItemLocal(item.id, { notes });
  };

  const setLegacyWishes = (value) => {
    parentFieldsRef.current = {
      ...(parentFieldsRef.current || {}),
      legacy_wishes: value,
    };
    scheduleParentSave(() => ({
      legacy_wishes: parentFieldsRef.current.legacy_wishes ?? '',
    }));
    bump((n) => n + 1);
  };

  const toggleNotes = (itemId) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Estate Checklist</h2>
          <p className="text-xs text-gray-500 mt-1">
            {needsAttentionCount} of 8 need attention
          </p>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden mt-2">
            <div
              className="h-full rounded-full bg-amber-500 transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(0, needsAttentionPct))}%` }}
            />
          </div>
        </div>

        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
          {orderedItems.map((item) => {
            const notesOpen = expandedNotes.has(item.id) || Boolean(item.notes);
            const label = ESTATE_ITEM_LABELS[item.item_key] || item.item_key;
            const flagged = Boolean(item.needs_attention);
            return (
              <li
                key={item.id}
                className={`px-4 py-3 ${
                  flagged ? 'bg-amber-50/80 border-l-2 border-l-amber-400' : 'bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    checked={flagged}
                    onChange={(e) => onToggleNeedsAttention(item, e.target.checked)}
                    aria-label={`Needs this: ${label}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <p className={`text-sm font-medium ${flagged ? 'text-amber-950' : 'text-gray-900'}`}>
                          {label}
                        </p>
                        {flagged && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800">
                            Needs This
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {flagged && item.last_updated && (
                          <span className="text-xs text-amber-700/70">
                            Flagged {formatDisplayDate(item.last_updated)}
                          </span>
                        )}
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                          onClick={() => toggleNotes(item.id)}
                        >
                          <SafeIcon
                            icon={notesOpen ? FiChevronDown : FiChevronRight}
                            className="w-3.5 h-3.5"
                          />
                          Notes
                        </button>
                      </div>
                    </div>
                    {notesOpen && (
                      <textarea
                        className={`${cellInput} mt-2 min-h-[64px] resize-y`}
                        placeholder="Optional notes…"
                        value={item.notes || ''}
                        onChange={(e) => onNotesChange(item, e.target.value)}
                      />
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {orderedItems.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">
            Checklist items missing — reopen this analysis or contact support.
          </p>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Legacy Wishes</h2>
          <p className="text-xs text-gray-500 mt-1">
            Document legacy intentions, special bequests, or guidance for beneficiaries.
          </p>
        </div>
        <textarea
          className={`${cellInput} min-h-[120px] resize-y`}
          placeholder="Legacy wishes…"
          value={legacyWishes || ''}
          onChange={(e) => setLegacyWishes(e.target.value)}
        />
      </Card>

      <Card className="p-5 border-2 border-amber-200 bg-amber-50/60 space-y-3">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-600" />
          <h2 className="text-base font-semibold text-amber-900">Key Recommendations</h2>
        </div>
        <p className="text-xs text-amber-800/80">
          Actionable guidance based on cashflow, balance sheet, insurance need, and estate checklist.
        </p>
        <ul className="space-y-2">
          {recommendations.map((text, idx) => (
            <li
              key={idx}
              className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2.5 text-sm text-gray-800"
            >
              {text}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
